import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import styled, {css} from 'styled-components';

import {
    mobileMediaQuery,
    sizes,
    tabletMediaQuery,
    useBreakpoint,
} from '../../styles';

import {CartoonitoLink} from '../CartoonitoLink';

import {ReactComponent as CloseIcon} from '../../assets/close.svg';
import ParentalIcon from '../../assets/ic_parents_white.png';
import * as braze from '@braze/web-sdk';
import DrawerPushView from './DrawerPushView';
import {debugCNitoUrl, useBrand} from '../../helpers';
import AgeRatingView from './AgeRatingView';
import {useSelector} from 'react-redux';

function DrawerMenu({defaultState, callback}) {
    const {t} = useTranslation();

    const breakpoint = useBreakpoint();

    const brand = useBrand();

    const bodyElement = document.querySelector('body');

    const user = useSelector(state => state.user);
    const history = useHistory();

    const [open, setOpen] = useState(defaultState || false);
    const pushSupported = braze.isPushSupported();
    const [showAgeRating, setShowAgeRating] = useState(true);

    useEffect(() => {
        if (open !== defaultState) {
            setOpen(defaultState);
        }

        // eslint-disable-next-line
    }, [defaultState]);

    useEffect(() => {
        if (open) {
            let top = window.scrollY;

            bodyElement.style.position = 'fixed';
            bodyElement.style.top = `-${top}px`;
            bodyElement.style.left = '0px';
            bodyElement.style.right = '0px';
        } else {
            const scrollY = bodyElement.style.top;

            bodyElement.style.position = '';
            bodyElement.style.top = '';
            bodyElement.style.left = '';
            bodyElement.style.right = '';

            window.scrollTo(0, -parseInt(scrollY || '0'));
        }
        setShowAgeRating(false);
        // eslint-disable-next-line
    }, [open]);

    const handleCloseMenu = () => {
        setOpen(false);
        callback && callback(false);
    };

    const setAge = () => {
        setShowAgeRating(true);
    };

    const openFavorites = () => {
        handleCloseMenu();
        history.push(`/favourites${debugCNitoUrl()}`);
    };

    return (
        <Container open={open}>
            <Backdrop open={open} onClick={handleCloseMenu} />

            {brand === 'cnapp' ? (
                <MenuBox>
                    <ArrowUp />
                    <MainContent>
                        <MenuButton onClick={setAge} alternate>
                            {t('ageRating.title')}
                            <img
                                alt={t('lockedContent.drawer.cta')}
                                src={ParentalIcon}
                                width="22px"
                                height="22px"
                            />
                        </MenuButton>
                        {pushSupported && <DrawerPushView open={open} />}
                    </MainContent>

                    <AgeRatingView
                        goBack={() => setShowAgeRating(false)}
                        show={showAgeRating}
                    />
                </MenuBox>
            ) : (
                <Menu open={open}>
                    <CloseButton onClick={handleCloseMenu}>
                        <CloseIcon title={t('controls.close')} />
                    </CloseButton>
                    <SideBlob src={require('../../assets/blob_drawer.png')} />

                    {['xs', 'mobile', 'sm', 'md'].includes(breakpoint) && (
                        <CartoonitoLink />
                    )}
                </Menu>
            )}
        </Container>
    );
}

const Container = styled.aside`
    position: fixed;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    z-index: 999;
    opacity: 0;
    transition: 0.3s all ease-in-out;
    pointer-events: none;

    ${({open}) =>
        open &&
        css`
            opacity: 1;
            pointer-events: initial;
        `}
`;

const ArrowUp = styled.img.attrs({
    src: require('../../assets/drawer_arrow.png'),
})`
    position: absolute;
    top: -10px;
    right: 25px;

    @media (min-width: ${sizes.xlarge}) {
        right: 70px;
    }
`;

const MainContent = styled.div`
    width: 311px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

const Backdrop = styled.div`
    width: 100%;
    height: 100%;

    transition: 0.3s all ease-in-out;

    ${({open}) => open && css``}
`;

const CloseButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0px;
    right: 0px;
    margin: 28px 44px 0 0;

    height: 20px;
    width: 20px;
    box-sizing: content-box;

    ${tabletMediaQuery} {
        margin: 28px 44px 0 0;
    }

    ${mobileMediaQuery} {
        margin: 28px 28px 0 0;
    }

    border: none;
    background-color: transparent;
    padding: 0;
    cursor: pointer;

    svg {
        width: 20px;
        height: 20px;
        g {
            fill: #a8a4a8;
        }
    }
`;

const Menu = styled.nav`
    position: fixed;
    top: 0px;
    right: -318px;
    background: rgb(0, 0, 0);
    height: 100%;
    width: 318px;
    max-width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 130px 32px 56px 32px;

    transition: 0.3s all ease-in-out;

    ${mobileMediaQuery} {
        padding: 96px 32px 56px 32px;
    }

    ${({open}) =>
        open &&
        css`
            right: 0px;
        `}

    li {
        width: 100%;
        list-style: none;

        a,
        button {
            text-decoration: none;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            width: 100%;
            margin: 0;
            padding-bottom: 16px;
            border-bottom: 1px solid rgb(58, 51, 58);
            padding: none;
            color: #fff;
            font-size: 18px;
            font-weight: 600;
            line-height: 31px;

            ${mobileMediaQuery} {
                font-size: 16px;
            }

            img {
                object-fit: contain;
                width: 28px;
                height: 28px;
                margin-right: 14px;
            }

            :not(:first-child) {
                padding-top: 16px;
            }
        }

        :first-of-type {
            margin-bottom: 40px;
        }

        :last-of-type {
            a {
                font-size: 14px;
                font-weight: initial;
                height: 24px;
                line-height: 24px;
                text-decoration: underline;
                width: auto;
                border: none;
                padding: 0;

                :not(:last-child) {
                    margin-bottom: 12px;
                }
            }
        }
    }
`;

export const LinkButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    background-color: transparent;
    border: none;
    padding: 0;
    margin: 0;
    margin-top: auto;
    padding: none;
    color: #fff;
    font-size: 18px;
    font-weight: 600;
    line-height: 31px;
    cursor: pointer;

    ${mobileMediaQuery} {
        font-size: 16px;
    }

    img {
        object-fit: contain;
        width: 28px;
        height: 28px;
        margin-right: 14px;
    }
`;

const SideBlob = styled.img`
    position: absolute;
    right: 0px;
    top: 34px;
`;

const MenuBox = styled.div`
    position: fixed;
    top: 95px;
    right: calc(50% - min(50vw, 720px) + 26px);
    width: 311px;
    background-color: #363036;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    border-radius: 4px;
    filter: drop-shadow(0px 0px 15px rgba(0, 0, 0, 0.35));

    clip-path: inset(-20px 0px -600px 0px);

    ${tabletMediaQuery} {
        top: 65px;
        right: 22px;
    }

    ${mobileMediaQuery} {
        top: 65px;
        right: 8px;
    }
`;

const Line = styled.div`
    width: 100%;
    height: 1px;
    background-color: white;
    opacity: 0.15;
`;

const MenuButton = styled(LinkButton)`
    font-family: AvenirNextRoundedStd;
    font-weight: 700;
    color: white;
    height: 62px;
    display: flex;
    padding: 0px 18px;
    justify-content: space-between;
    align-self: stretch;
    text-transform: uppercase;
    img {
        object-fit: contain;
        width: 22px;
        height: 22px;
        margin: 0px;
    }
`;

export default DrawerMenu;
